import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Badge,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../api';
import Navigation from '../components/Navigation';

interface Conversation {
  match_id: string;
  other_user_id: string;
  other_url_id: string;
  other_photo_url: string | null;
  other_tags: string[];
  created_at: string | null;
  last_activity_at: string | null;
  unread_count: number;
  total_messages: number;
  nudge_active: boolean;
}

interface ConversationStatus {
  active_count: number;
  max_conversations: number;
  at_capacity: boolean;
  has_pending_feedback: boolean;
  pending_feedback: Array<{ match_id: string }>;
  pending_match_count: number;
}

interface ReceivedFarewell {
  match_id: string;
  other_user_id: string;
  other_url_id: string | null;
  other_photo_url: string | null;
  farewell: {
    type: string | null;
    audio_url?: string;
    text?: string;
  };
  closed_at: string | null;
}

const HIDDEN_KEY = 'seriously-hidden-farewells';
const REMOVED_KEY = 'seriously-removed-farewells';

function getStoredSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function storeSet(key: string, set: Set<string>) {
  localStorage.setItem(key, JSON.stringify(Array.from(set)));
}

const Conversations: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const convQ = useQuery({
    queryKey: ['conversations'],
    queryFn: () => api.get('/api/conversations').then(r => r.data.conversations as Conversation[]),
  });
  const statusQ = useQuery({
    queryKey: ['conversations', 'status'],
    queryFn: () => api.get('/api/conversations/status').then(r => r.data as ConversationStatus),
  });
  const farewellsQ = useQuery({
    queryKey: ['conversations', 'farewells'],
    queryFn: () => api.get('/api/conversations/farewells').then(r => r.data.farewells as ReceivedFarewell[]),
  });

  const loading = convQ.isLoading || statusQ.isLoading || farewellsQ.isLoading;
  const conversations = convQ.data ?? [];
  const status = statusQ.data ?? null;
  const farewells = farewellsQ.data ?? [];
  const queryErr = convQ.error || statusQ.error || farewellsQ.error;
  const displayError = error || (queryErr ? ((queryErr as any)?.response?.data?.detail || 'Failed to load conversations') : '');

  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => getStoredSet(HIDDEN_KEY));
  const [removedIds, setRemovedIds] = useState<Set<string>>(() => getStoredSet(REMOVED_KEY));
  const [allHidden, setAllHidden] = useState(false);

  // Three-dot menu & end conversation
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuMatchId, setMenuMatchId] = useState<string | null>(null);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [ending, setEnding] = useState(false);

  const visibleFarewells = farewells.filter(
    (f) => !removedIds.has(f.match_id) && !allHidden && !hiddenIds.has(f.match_id)
  );
  const nonRemovedFarewells = farewells.filter((f) => !removedIds.has(f.match_id));

  const toggleHide = useCallback((matchId: string) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(matchId)) next.delete(matchId);
      else next.add(matchId);
      storeSet(HIDDEN_KEY, next);
      return next;
    });
  }, []);

  const removePermanently = useCallback((matchId: string) => {
    setRemovedIds((prev) => {
      const next = new Set(prev);
      next.add(matchId);
      storeSet(REMOVED_KEY, next);
      return next;
    });
    // Also clean from hidden
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.delete(matchId);
      storeSet(HIDDEN_KEY, next);
      return next;
    });
  }, []);

  const handleHideAll = () => {
    setAllHidden(true);
  };

  const handleShowAll = () => {
    setAllHidden(false);
    setHiddenIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      storeSet(HIDDEN_KEY, next);
      return next;
    });
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, matchId: string) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuMatchId(matchId);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuMatchId(null);
  };

  const handleEndConversation = async () => {
    if (!menuMatchId) return;
    setEnding(true);
    try {
      await api.post(`/api/conversations/${menuMatchId}/unmatch`);
      setEndDialogOpen(false);
      closeMenu();
      navigate(`/conversations/${menuMatchId}/feedback`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to end conversation');
      setEndDialogOpen(false);
      closeMenu();
    } finally {
      setEnding(false);
    }
  };

  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const hasContent = conversations.length > 0 || nonRemovedFarewells.length > 0;

  return (
    <>
      <Navigation />
      <Container maxWidth="sm" sx={{ mt: 3, mb: 3 }}>
        <Typography variant="caption" sx={{ color: "primary.main", mb: 2, display: "block", textAlign: "center" }}>
          Conversations
        </Typography>

        {status && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            {status.active_count}/{status.max_conversations} active conversations
          </Typography>
        )}

        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {displayError}
          </Alert>
        )}

        {/* Pending feedback banner */}
        {status?.has_pending_feedback && status.pending_feedback.length > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() =>
                  navigate(`/conversations/${status.pending_feedback[0].match_id}/feedback`)
                }
              >
                Complete Feedback
              </Button>
            }
          >
            You have pending feedback to complete before you can use Discover or edit your profile.
          </Alert>
        )}

        {/* Pending matches info */}
        {status && status.pending_match_count > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {status.pending_match_count} pending match{status.pending_match_count > 1 ? 'es' : ''}{' '}
            waiting for a free slot.
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress sx={{ color: "primary.main" }} />
          </Box>
        )}

        {/* Empty state — no conversations and no farewells */}
        {!loading && !hasContent && (
          <Paper sx={{ p: 2.5, textAlign: 'center', mt: 3 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 32, color: "divider", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No conversations yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              When you and another person both like each other, a conversation will start here.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/discover')}
            >
              Go to Discover
            </Button>
          </Paper>
        )}

        {/* Active conversations */}
        {!loading &&
          conversations.map((conv) => (
            <Paper
              key={conv.match_id}
              sx={{
                p: 2,
                mb: 1.5,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 0.2s',
                '&:hover': { backgroundColor: "error.light" },
              }}
              onClick={() => navigate(`/conversations/${conv.match_id}`)}
            >
              <Badge
                badgeContent={conv.unread_count}
                color="error"
                invisible={conv.unread_count === 0}
              >
                <Avatar
                  src={conv.other_photo_url || undefined}
                  alt={conv.other_url_id ? `${conv.other_url_id}'s photo` : 'User photo'}
                  sx={{ width: 40, height: 40 }}
                />
              </Badge>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: conv.unread_count > 0 ? 'bold' : 'normal' }}>
                  {conv.other_url_id || 'Someone'}
                </Typography>
                {conv.other_tags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {conv.other_tags.slice(0, 3).map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
              </Box>

              <Box sx={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(conv.last_activity_at || conv.created_at)}
                  </Typography>
                  <ArrowForwardIosIcon sx={{ fontSize: 14, color: '#9CA3AF', display: 'block', ml: 'auto', mt: 0.5 }} />
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => openMenu(e, conv.match_id)}
                  sx={{ color: '#9CA3AF' }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}

        {/* Farewell history section */}
        {!loading && nonRemovedFarewells.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Farewell Messages
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {!allHidden ? (
                  <Button
                    size="small"
                    startIcon={<VisibilityOffIcon />}
                    onClick={handleHideAll}
                    sx={{ color: 'text.secondary' }}
                  >
                    Hide All
                  </Button>
                ) : (
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={handleShowAll}
                    sx={{ color: 'text.secondary' }}
                  >
                    Show All
                  </Button>
                )}
              </Box>
            </Box>

            {allHidden && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                {nonRemovedFarewells.length} farewell message{nonRemovedFarewells.length > 1 ? 's' : ''} hidden.{' '}
                <Button size="small" onClick={handleShowAll} sx={{ p: 0, minWidth: 0 }}>
                  Show all
                </Button>
              </Typography>
            )}

            {!allHidden &&
              nonRemovedFarewells.map((fw) => {
                const isHidden = hiddenIds.has(fw.match_id);

                return (
                  <Paper
                    key={fw.match_id}
                    sx={{ p: 2, mb: 1.5 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={fw.other_photo_url || undefined}
                        alt={fw.other_url_id ? `${fw.other_url_id}'s photo` : 'User photo'}
                        sx={{ width: 48, height: 48 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2">
                          {fw.other_url_id || 'Someone'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(fw.closed_at)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={isHidden ? 'Show' : 'Hide'}>
                          <IconButton size="small" onClick={() => toggleHide(fw.match_id)}>
                            {isHidden ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove permanently">
                          <IconButton size="small" onClick={() => removePermanently(fw.match_id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Farewell content — hidden when toggled */}
                    {!isHidden && (
                      <Box sx={{ mt: 1.5, pl: 8 }}>
                        {fw.farewell.type === 'voice' && fw.farewell.audio_url && (
                          <audio
                            controls
                            src={fw.farewell.audio_url}
                            style={{ width: '100%', maxWidth: 320 }}
                          />
                        )}
                        {fw.farewell.type === 'text' && fw.farewell.text && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontStyle: 'italic',
                              color: 'text.secondary',
                              backgroundColor: "secondary.main",
                              p: 1.5,
                              borderLeft: '3px solid',
                              borderColor: "primary.main",
                            }}
                          >
                            "{fw.farewell.text}"
                          </Typography>
                        )}
                        {!fw.farewell.type && (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            No farewell message left.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Paper>
                );
              })}
          </>
        )}
      </Container>

      {/* Three-dot context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setEndDialogOpen(true);
            setMenuAnchor(null);
          }}
          sx={{ color: "primary.main" }}
        >
          End conversation
        </MenuItem>
      </Menu>

      {/* End conversation confirmation */}
      <Dialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)}>
        <DialogTitle sx={{ color: "primary.main", fontWeight: 'bold' }}>
          End this conversation?
        </DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently end the conversation. You'll both be asked to leave a farewell message.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEndDialogOpen(false); closeMenu(); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={ending}
            onClick={handleEndConversation}
          >
            {ending ? <CircularProgress size={20} /> : 'End Conversation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Conversations;
